<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">用户列表</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ totalUsers }}</b> 位用户</span>
        <span class="divider">|</span>
        <span>正常 <b>{{ statusCount(1) }}</b></span>
        <span class="divider">|</span>
        <span>已禁用 <b>{{ statusCount(0) }}</b></span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="关键字">
            <el-input
              v-model="query.keyword"
              placeholder="昵称 / 手机号"
              clearable
              style="width: 180px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 120px">
              <el-option v-for="s in USER_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="注册时间">
            <el-date-picker
              v-model="query.dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button icon="el-icon-download" size="small" @click="handleExport">导出名单</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <!-- 用户 -->
        <el-table-column label="用户" min-width="160">
          <template slot-scope="scope">
            <div class="user-cell">
              <el-image
                v-if="scope.row.avatar"
                class="user-avatar user-avatar--img"
                :src="scope.row.avatar"
                fit="cover"
              >
                <span slot="error" class="user-avatar" :class="{ 'user-avatar--female': scope.row.gender === 2 }">
                  {{ (scope.row.nickname || '客')[0] }}
                </span>
              </el-image>
              <span v-else class="user-avatar" :class="{ 'user-avatar--female': scope.row.gender === 2 }">
                {{ (scope.row.nickname || '客')[0] }}
              </span>
              <span class="user-name">
                {{ scope.row.nickname || '未设置昵称' }}
                <i v-if="scope.row.real_name" class="el-icon-s-check real-mark" title="已填写真实姓名" />
              </span>
            </div>
          </template>
        </el-table-column>
        <!-- 手机号 -->
        <el-table-column label="手机号" width="130" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.phone">{{ scope.row.phone }}</span>
            <span v-else class="cell-muted">未授权</span>
          </template>
        </el-table-column>
        <!-- 性别 -->
        <el-table-column label="性别" width="70" align="center">
          <template slot-scope="scope">{{ genderText(scope.row.gender) }}</template>
        </el-table-column>
        <!-- 地区 -->
        <el-table-column label="地区" min-width="110" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.region">{{ scope.row.region }}</span>
            <span v-else class="cell-muted">—</span>
          </template>
        </el-table-column>
        <!-- 推荐人（推广员工，referrer_id=0 表示无归属） -->
        <el-table-column label="推荐人" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <div v-if="scope.row.referrer_name" class="referrer-cell">
              <span class="referrer-name">{{ scope.row.referrer_name }}</span>
              <span v-if="scope.row.referrer_company_name" class="referrer-company">
                {{ scope.row.referrer_company_name }}
              </span>
            </div>
            <span v-else class="cell-muted">—</span>
          </template>
        </el-table-column>
        <!-- 社区数据 -->
        <el-table-column label="已发布" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.post_count }}</template>
        </el-table-column>
        <el-table-column label="获赞" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.like_count }}</template>
        </el-table-column>
        <el-table-column label="关注" width="70" align="center">
          <template slot-scope="scope">{{ scope.row.follow_count }}</template>
        </el-table-column>
        <el-table-column label="粉丝" width="70" align="center">
          <template slot-scope="scope">{{ scope.row.fans_count }}</template>
        </el-table-column>
        <!-- 状态 -->
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="userStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ userStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 注册时间 -->
        <el-table-column label="注册时间" width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.created_at }}</template>
        </el-table-column>
        <!-- 最近登录 -->
        <el-table-column label="最近登录" width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.last_login_at }}</template>
        </el-table-column>
        <!-- 操作 -->
        <el-table-column label="操作" width="180" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleDetail(scope.row)">详情</el-button>
            <el-button type="text" size="mini" @click="handleReferrer(scope.row)">改推荐人</el-button>
            <el-button
              v-if="scope.row.status === 1"
              type="text" size="mini" class="text-warning-btn"
              @click="handleStatus(scope.row, 0)"
            >禁用</el-button>
            <el-button
              v-else
              type="text" size="mini"
              @click="handleStatus(scope.row, 1)"
            >启用</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 用户详情弹框 -->
    <el-dialog
      title="用户详情"
      :visible.sync="detailVisible"
      width="640px"
      top="6vh"
      class="user-detail-dialog"
      append-to-body
    >
      <div v-if="detailRow" class="detail-wrap">
        <div class="detail-head">
          <el-image
            v-if="detailRow.avatar"
            class="detail-avatar detail-avatar--img"
            :src="detailRow.avatar"
            fit="cover"
          >
            <span slot="error" class="detail-avatar" :class="{ 'user-avatar--female': detailRow.gender === 2 }">
              {{ (detailRow.nickname || '客')[0] }}
            </span>
          </el-image>
          <span v-else class="detail-avatar" :class="{ 'user-avatar--female': detailRow.gender === 2 }">
            {{ (detailRow.nickname || '客')[0] }}
          </span>
          <div class="detail-head-main">
            <div class="detail-nick">
              {{ detailRow.nickname || '未设置昵称' }}
              <el-tag :type="userStatusTagType(detailRow.status)" size="mini" effect="plain">
                {{ userStatusText(detailRow.status) }}
              </el-tag>
            </div>
            <div class="detail-sub">用户编号 {{ detailRow.id }} · 注册于 {{ detailRow.created_at }}</div>
          </div>
        </div>

        <div class="detail-stats">
          <div class="stat-cell">
            <b>{{ detailRow.post_count }}</b>
            <span>已发布</span>
          </div>
          <div class="stat-cell">
            <b>{{ detailRow.like_count }}</b>
            <span>获赞</span>
          </div>
          <div class="stat-cell">
            <b>{{ detailRow.favorite_count }}</b>
            <span>已收藏</span>
          </div>
          <div class="stat-cell">
            <b>{{ detailRow.follow_count }}</b>
            <span>关注</span>
          </div>
          <div class="stat-cell">
            <b>{{ detailRow.fans_count }}</b>
            <span>粉丝</span>
          </div>
        </div>

        <div class="detail-fields">
          <div class="field-row">
            <span class="field-label">手机号</span>
            <span>{{ detailRow.phone || '未授权' }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">真实姓名</span>
            <span>{{ detailRow.real_name || '未填写' }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">性别</span>
            <span>{{ genderText(detailRow.gender) }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">地区</span>
            <span>{{ detailRow.region || '未知' }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">推荐人</span>
            <span v-if="detailRow.referrer_name">
              {{ detailRow.referrer_name }}
              <span v-if="detailRow.referrer_company_name" class="cell-muted">
                · {{ detailRow.referrer_company_name }}
              </span>
            </span>
            <span v-else class="cell-muted">无（未绑定推广员工）</span>
          </div>
          <div class="field-row">
            <span class="field-label">最近登录</span>
            <span>{{ detailRow.last_login_at }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">登录地址</span>
            <span>{{ detailRow.last_login_ip || '—' }}</span>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 改推荐人弹框：候选为员工（不限岗位），清空即解除绑定 -->
    <el-dialog
      title="修改推荐人"
      :visible.sync="referrerVisible"
      width="480px"
      append-to-body
      @close="resetReferrer"
    >
      <div v-if="referrerRow" class="referrer-tip">
        为「{{ referrerRow.nickname || '未设置昵称' }}」指定推广员工，指定后该用户的归属统计计入该员工；
        清空表示不绑定。
      </div>
      <el-form :model="referrerForm" label-width="72px" size="small" class="referrer-form">
        <el-form-item label="推荐人">
          <el-select
            v-model="referrerForm.referrer_id"
            filterable
            clearable
            :loading="referrerLoading"
            placeholder="选择员工"
            style="width: 100%"
          >
            <el-option
              v-for="p in referrerOptions"
              :key="p.id"
              :label="personOptionLabel(p)"
              :value="p.id"
            >
              <span class="referrer-opt-name">{{ p.name }}</span>
              <span v-if="p.company_name" class="referrer-opt-company">{{ p.company_name }}</span>
              <span v-if="p.status === 0" class="referrer-opt-off">已停用</span>
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="referrerVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :loading="referrerSaving" @click="submitReferrer">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { USER_STATUS, userStatusText, userStatusTagType, genderText } from '@/mock/user'
import { getUserList, getUserDetail, setUserStatus, setUserReferrer } from '@/api/user'
import { getPersonOptions } from '@/api/system'

export default {
  name: 'UserListManage',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      // 头部统计（后端全量计数，不受当页筛选影响）
      statusCounts: {},
      query: { keyword: '', status: '', dateRange: null },
      detailVisible: false,
      detailRow: null,
      // 改推荐人
      referrerVisible: false,
      referrerRow: null,
      referrerSaving: false,
      referrerLoading: false,
      referrerOptions: [],
      referrerForm: { referrer_id: '' },
      USER_STATUS,
      userStatusText,
      userStatusTagType,
      genderText
    }
  },
  computed: {
    totalUsers() {
      return (this.statusCounts['1'] || 0) + (this.statusCounts['0'] || 0)
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      const params = {
        page: this.page,
        page_size: this.pageSize
      }
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status
      if (this.query.dateRange && this.query.dateRange.length === 2) {
        params.date_range = this.query.dateRange
      }
      getUserList(params).then(res => {
        if (res.code !== 200) return
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.statusCounts = res.data.status_counts || {}
      }).finally(() => {
        this.loading = false
      })
    },
    statusCount(val) { return this.statusCounts[String(val)] || 0 },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', status: '', dateRange: null }
      this.page = 1
      this.loadData()
    },
    handleExport() {
      // 导出名单（POST /user/export）规格待确认（同步/异步），后端落地后再接
      this.$message.info('导出名单接口待后端确认导出方案后开放')
    },
    handleDetail(row) {
      this.detailRow = row
      this.detailVisible = true
      // 拉最新详情（含 auth_status），失败时保留列表行数据
      getUserDetail(row.id).then(res => {
        if (res.code === 200 && res.data && this.detailRow && this.detailRow.id === row.id) {
          this.detailRow = res.data
        }
      })
    },
    handleStatus(row, status) {
      const action = status === 1 ? '启用' : '禁用'
      const tip = status === 1
        ? `确认启用「${row.nickname || '该用户'}」？启用后可正常登录小程序。`
        : `确认禁用「${row.nickname || '该用户'}」？禁用后将无法登录小程序，已发布内容保留。`
      this.$confirm(tip, action + '确认', { type: 'warning' })
        .then(() => {
          setUserStatus(row.id, { status }).then(res => {
            if (res.code !== 200) return
            this.$message.success('已' + action)
            this.loadData()
            if (this.detailRow && this.detailRow.id === row.id) {
              this.detailRow = Object.assign({}, this.detailRow, { status })
            }
          })
        }).catch(() => {})
    },
    // ===== 改推荐人 =====
    // 候选员工由后端按账号范围过滤：主账号看全部，企业账号只出自己公司及下级
    handleReferrer(row) {
      this.referrerRow = row
      this.referrerForm = { referrer_id: row.referrer_id ? row.referrer_id : '' }
      this.referrerVisible = true
      this.loadReferrerOptions()
    },
    loadReferrerOptions() {
      if (this.referrerOptions.length) return
      this.referrerLoading = true
      getPersonOptions().then(res => {
        if (res.code !== 200) return
        this.referrerOptions = (res.data && res.data.list) || []
      }).finally(() => {
        this.referrerLoading = false
      })
    },
    personOptionLabel(p) {
      return p.company_name ? `${p.name}（${p.company_name}）` : p.name
    },
    submitReferrer() {
      if (!this.referrerRow) return
      const referrerId = Number(this.referrerForm.referrer_id) || 0
      this.referrerSaving = true
      setUserReferrer(this.referrerRow.id, { referrer_id: referrerId }).then(res => {
        if (res.code !== 200) return
        this.$message.success(referrerId > 0 ? '推荐人已更新' : '已解除推荐人绑定')
        this.referrerVisible = false
        this.loadData()
      }).finally(() => {
        this.referrerSaving = false
      })
    },
    resetReferrer() {
      this.referrerForm = { referrer_id: '' }
      this.referrerRow = null
    }
  }
}
</script>

<style scoped>
.user-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.user-avatar {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #6b5a45;
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.user-avatar--female {
  background: #a4686b;
}
/* 真实头像：el-image 内图吃满圆框 */
.user-avatar--img >>> img,
.detail-avatar--img >>> img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}
.user-name {
  font-size: 13px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.real-mark {
  color: var(--primary);
  font-size: 14px;
}
.cell-muted {
  color: #c0c4cc;
  font-size: 12px;
}
.referrer-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.referrer-name {
  font-size: 13px;
  color: #303133;
}
.referrer-company {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 详情弹框 */
.user-detail-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}
.detail-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}
.detail-avatar {
  flex: 0 0 56px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #6b5a45;
  color: #fff;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.detail-head-main {
  min-width: 0;
}
.detail-nick {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}
.detail-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.detail-stats {
  display: flex;
  border: 1px solid #f0ebe2;
  border-radius: 8px;
  background: #faf7f1;
  margin-bottom: 14px;
}
.stat-cell {
  flex: 1;
  text-align: center;
  padding: 12px 0;
}
.stat-cell + .stat-cell {
  border-left: 1px solid #f0ebe2;
}
.stat-cell b {
  display: block;
  font-size: 18px;
  color: #303133;
}
.stat-cell span {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}
.detail-fields {
  border-top: 1px solid #f0f0f0;
}
.field-row {
  display: flex;
  padding: 9px 0;
  border-bottom: 1px dashed #f0f0f0;
  font-size: 13px;
  color: #303133;
}
.field-row:last-child {
  border-bottom: none;
}
.field-label {
  flex: 0 0 90px;
  color: #909399;
}

/* 改推荐人弹框 */
.referrer-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #faf7f1;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 14px;
}
.referrer-form >>> .el-form-item {
  margin-bottom: 0;
}
.referrer-opt-name {
  font-size: 13px;
  color: #303133;
}
.referrer-opt-company {
  margin-left: 6px;
  font-size: 12px;
  color: #909399;
}
.referrer-opt-off {
  margin-left: 6px;
  font-size: 12px;
  color: #c0c4cc;
}
</style>
